import { CSSProperties, useMemo } from 'react';
import { CurrencyFormatterOptions } from 'react-formatted-input-hook';

import {
  CURRENCIES_META,
  type CurrencyCodeType,
} from '@mint-lib/currencies-helpers';
import { useTranslation } from '@mint-lib/i18n';
import {
  useProfile,
  type CurrencyFormatType,
  type ProfileLocale,
} from '@mint-lib/profile-helpers';

import { useNumberFormatter } from '../StaticNumericSpan/StaticNumericSpan.jsx';

const CURRENCY_FORMAT_MAP: Record<ProfileLocale, CurrencyFormatType> = {
  'en-GB': 'before',
  'en-US': 'before',
  'pt-BR': 'before',
  'it-IT': 'after',
  FAKE: 'after',
};

export type Params = {
  currency: string;
  options?: Omit<
    CurrencyFormatterOptions,
    'liveUpdate' | 'onChange' | 'value' | 'currency'
  > & {
    minDecimals?: number;
  };
};

/**
 * A hook that returns a currency formatter function based on the provided currency and options.
 * @param currency - The currency code to use in formatting.
 * @param options - Optional configuration for the currency formatter.
 * @param options.minDecimals - The minimum number of decimal places to display.
 * @param options.style - The currency formatting style to use.
 * @param options.useGrouping - Whether to use grouping separators (e.g. thousands separators).
 * @param options.currencyDisplay - How to display the currency symbol.
 * @param options.currencySign - The sign to use for the currency.
 * @param options.localeMatcher - The locale matching algorithm to use.
 * @returns A currency formatter function.
 * @throws Error if the currency is not in standard currency format like USD.
 */
export const useCurrencyFormatter = ({ currency, options = {} }: Params) => {
  const { i18n } = useTranslation('@myn/mui');

  const { getProfileField } = useProfile();

  const threeCapitalLetter = /^[A-Z]{3}$/g;

  if (!threeCapitalLetter.test(currency)) {
    throw Error('Currency should be in standard currency format like USD');
  }

  const locale = getProfileField('locale')
    ? getProfileField('locale')
    : i18n.language;

  const currencyFormat =
    getProfileField('formattingConfig')?.currencyFormat ||
    CURRENCY_FORMAT_MAP[locale as ProfileLocale];

  const currencySymbol = CURRENCIES_META[currency as CurrencyCodeType].symbol;

  const { format, value: valueFmt } = useNumberFormatter({
    options: {
      minDecimals: 2,
      prefix: currencyFormat === 'before' ? currencySymbol : undefined,
      suffix: currencyFormat === 'after' ? ` ${currencySymbol}` : undefined,
      ...options,
      // temporary fix when 0 passed to options
      // see campaigns ColumnFields/hooks/useGetBudgetFieldInfo.tsx
      maxDecimals: options?.maxDecimals || 2,
    },
  });

  return useMemo(() => {
    return {
      format: (value: string | number) => {
        const mapping = format?.(value ? `${value}` : '')?.mapping || [];

        return {
          formatted: format(value ? `${value}` : '')?.formatted ?? '',
          mapping,
        };
      },
      value: valueFmt,
    };
  }, [currencyFormat, currency, ...Object.values(options), format]);
};

/**
 * A component that displays a static currency value.
 * @param number - The number to format as currency.
 * @param currency - The currency code to use in formatting.
 * @param sx - Optional styling for the `span` element.
 * @param options - Optional configuration for the currency formatter.
 * @param options.minDecimals - The minimum number of decimal places to display.
 * @param options.style - The currency formatting style to use.
 * @param options.useGrouping - Whether to use grouping separators (e.g. thousands separators).
 * @param options.currencyDisplay - How to display the currency symbol.
 * @param options.currencySign - The sign to use for the currency.
 * @param options.localeMatcher - The locale matching algorithm to use.
 * @param options.currencyDisplay - How to display the currency symbol.
 * @param options.currencySign - The sign to use for the currency.
 * @param options.localeMatcher - The locale matching algorithm to use.
 * @param options.currencyDisplay - How to display the currency symbol.
 * @param options.currencySign - The sign to use for the currency.
 * @param options.localeMatcher - The locale matching algorithm to use.
 * @returns A `span` element containing the formatted currency value.
 */
const StaticCurrencySpan = ({
  number,
  currency,
  options = {},
  sx = {},
}: {
  number: number | string;
  currency: string;
  sx?: CSSProperties;
  options?: Omit<
    CurrencyFormatterOptions,
    'liveUpdate' | 'onChange' | 'value' | 'currency'
  > & {
    minDecimals?: number;
  };
}) => {
  const formatter = useCurrencyFormatter({
    currency,
    options,
  });

  const res = formatter.format?.(`${number}`);

  return <span style={{ ...sx }}>{res?.formatted ?? ''}</span>;
};

export default StaticCurrencySpan;
